import type { BillingApi } from '@eis/billingservice-sdk';
import {
  addMonths,
  formatCivilDate,
  formatCurrency,
  formatPeriod,
  isApiError,
  refusalReasonOf,
  type InvoiceStatus,
  type InvoiceType,
} from '@eis/core';

/**
 * The invoice model of the console — what the screens derive from the legacy `Invoice` shape
 * (SDD-015 B-2) and never re-derive per screen: rows for the grid, labels for money and periods,
 * the two questions every action asks (is this a delta document? can it be reverted?), and the
 * month → period translation the list filter rests on.
 *
 * Money on the wire is an integer in MINOR units (STOP-2 interim), rendered through
 * `formatCurrency`; the five legacy doubles on an item are display values and are shown as they
 * came. Dates on the wire are RFC 3339 timestamps whose civil part is what a reader means.
 */
export const INVOICE_TYPES: readonly InvoiceType[] = [
  'initial',
  'recurring',
  'correction',
  'estimated',
  'final',
  'withdraw',
  'other',
  'penalty',
];

export const INVOICE_STATUSES: readonly InvoiceStatus[] = [
  'open',
  'paid',
  'partially-paid',
  'refunded',
  'estimated',
];

/** The types a reader filters the list by — the service's closed set minus the one it mints alone. */
export const FILTERABLE_TYPES: readonly InvoiceType[] = INVOICE_TYPES.filter((type) => type !== 'penalty');

export interface InvoiceRow {
  readonly id: string;
  readonly code: string;
  readonly number: string;
  readonly type: string;
  readonly status: string;
  readonly policyCode: string;
  readonly groupPolicyCode: string;
  readonly periodFrom: string;
  readonly periodTo: string;
  readonly dueDate: string;
  readonly grossMinor: number;
  readonly currency: string;
  readonly createdAt: string;
}

/** The civil part of a wire timestamp (`2026-07-01T00:00:00Z` → `2026-07-01`); `''` for none. */
export const civilOf = (value: string | undefined): string => (value ?? '').slice(0, 10);

export function toInvoiceRows(items: readonly BillingApi.Invoice[] | undefined): InvoiceRow[] {
  return (items ?? []).map((invoice) => ({
    id: invoice.code ?? '',
    code: invoice.code ?? '',
    number: invoice.invoiceNumber ?? '',
    type: invoice.type ?? '',
    status: invoice.status ?? '',
    policyCode: invoice.policyCode ?? '',
    groupPolicyCode: invoice.groupPolicyCode ?? '',
    periodFrom: civilOf(invoice.billingIntervalFrom),
    periodTo: civilOf(invoice.billingIntervalTo),
    dueDate: civilOf(invoice.dueDate),
    grossMinor: invoice.grossAmount ?? 0,
    currency: invoice.currency ?? 'EUR',
    createdAt: invoice.createdAt ?? '',
  }));
}

/**
 * Minor units → the reader's amount. `undefined` is "not stated" and renders as a dash, never as
 * 0,00 €: printing a number the document does not carry is the one outcome worse than nothing.
 */
export function formatMinor(minor: number | undefined, currency: string | undefined, locale: string): string {
  if (minor === undefined) return '—';
  return formatCurrency(minor, currency === undefined || currency === '' ? 'EUR' : currency, locale);
}

const isMonthStart = (civil: string): boolean => civil.endsWith('-01');

/**
 * A half-open period `[from, to)` in the reader's words: a whole calendar month is its name
 * ("Juli 2026"), anything else the first and the LAST covered day — the day before `to`, because
 * the period is half-open on the wire and inclusive in a sentence (§5.3).
 */
export function periodLabel(from: string, to: string, locale: string): string {
  if (from === '' || to === '') return from === '' ? '—' : formatCivilDate(from, locale);
  if (isMonthStart(from) && addMonths(from, 1) === to) return formatPeriod(from.slice(0, 7), locale);
  const lastDay = fromCalendarShift(to, -1);
  return `${formatCivilDate(from, locale)} – ${formatCivilDate(lastDay, locale)}`;
}

/** One civil day back or forward, on the string itself — no wall clock, no time zone. */
function fromCalendarShift(civil: string, days: number): string {
  const [y, m, d] = civil.split('-').map(Number);
  const shifted = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + days));
  return shifted.toISOString().slice(0, 10);
}

/** `YYYY-MM` → the half-open month the service filters by (`billingIntervalFrom ≥`, `billingIntervalTo ≤`). */
export function monthRange(month: string): { readonly periodFrom: string; readonly periodTo: string } | undefined {
  if (!/^\d{4}-\d{2}$/.test(month)) return undefined;
  const periodFrom = `${month}-01`;
  return { periodFrom, periodTo: addMonths(periodFrom, 1) };
}

/** A correction, a final or a withdraw invoice re-states money: its lines carry previous → new. */
export const isDeltaType = (type: string | undefined): boolean =>
  type === 'correction' || type === 'final' || type === 'withdraw';

/**
 * What the service will refuse to revert (§6.3): an estimate (not a document), a refunded invoice
 * (already reverted) and a reversal (reverting a reversal would re-issue by the back door).
 */
export const isRevertible = (invoice: BillingApi.Invoice | undefined): boolean =>
  invoice !== undefined &&
  invoice.type !== 'estimated' &&
  invoice.status !== 'refunded' &&
  (invoice.revertsInvoiceErn ?? '') === '';

/** The number a human quotes, or the code when the document has none (an estimate). */
export const invoiceTitle = (invoice: BillingApi.Invoice | undefined): string =>
  invoice?.invoiceNumber === undefined || invoice.invoiceNumber === '' ? (invoice?.code ?? '') : invoice.invoiceNumber;

/** `billingservice:bill/bill_A1B2C3D4E5` → `bill_A1B2C3D4E5`; a code stays a code. */
export const codeOfErn = (ern: string | undefined): string => {
  const value = ern ?? '';
  const slash = value.lastIndexOf('/');
  return slash === -1 ? value : value.slice(slash + 1);
};

/**
 * The service's refusal, as the key the screen translates it under — or `undefined` for anything
 * that is not a reasoned refusal (an outage, a network failure, a 500), which the screen renders
 * from the error itself.
 */
export function refusalKeyOf(error: unknown): string | undefined {
  const reason = refusalReasonOf(error);
  if (reason !== undefined) return reason;
  if (isApiError(error) && error.status === 400) return 'INVALID_REQUEST';
  return undefined;
}
